/**
 * DASHBOARD HEADER COMPONENT
 * =========================
 * 
 * The top navigation bar for authenticated users. Contains:
 * - Page title with context (left-aligned)
 * - Quick actions, theme toggle, and user menu (right-aligned)
 * 
 * This component appears at the top of every authenticated page and provides
 * consistent navigation and branding throughout the app.
 * 
 * LAYOUT STRUCTURE:
 * - Page title and description: Left-aligned
 * - New Decision button + Theme toggle + User menu: Right-aligned
 */

import { useState } from "react";
import { Button } from "./ui/button";
import { Badge } from "./ui/badge";
import { Plus, User, Settings, LogOut, BookOpen, Crown, Zap, RefreshCw } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "./ui/tooltip";
import { toast } from "sonner";
import { useSubscription } from "@/hooks/useSubscription";
import { useDecisions } from "@/hooks/useDecisions";
import { useAuth } from "@/hooks/useAuth";
import { useMobile } from "../src/hooks/use-mobile";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import { ThemeToggle } from "./ThemeToggle";
import type { Page } from "../src/types";

// Define the possible pages to show appropriate titles

interface DashboardHeaderProps {
  onNewDecision: () => void;    // Function to navigate to new decision form
  onSignOut: () => void;        // Function to handle user logout
  currentPage: Page;            // Current active page for dynamic title
  onSettings?: () => void;      // Optional settings navigation
  onHelp?: () => void;          // Optional help navigation  
  onUpgrade?: () => void;       // Optional upgrade navigation
}

export function DashboardHeader({ onNewDecision, onSignOut, currentPage, onSettings, onHelp, onUpgrade }: DashboardHeaderProps) {
  const { subscription, refreshSubscription } = useSubscription();
  const { refreshDecisions } = useDecisions();
  const { user } = useAuth();
  const isMobile = useMobile();

  // Handle refresh of all user data
  const [isRefreshing, setIsRefreshing] = useState(false);
  
  const handleRefresh = async () => {
    setIsRefreshing(true);
    try {
      await Promise.all([
        refreshDecisions(),
        refreshSubscription()
      ]);
      toast.success('Data refreshed successfully');
    } catch (error) {
      console.error('Error refreshing data:', error);
      toast.error('Failed to refresh data');
    } finally {
      setIsRefreshing(false);
    }
  };

  
  /**
   * Get page title based on current page
   * Returns user-friendly titles for each section of the app
   */
  const getPageTitle = () => {
    switch (currentPage) {
      case "dashboard":
        return "Dashboard";
      case "new-decision":
        return "New Decision";
      case "my-decisions":
        return "My Decisions";
      case "decision-detail":
        return "Decision Details";
      case "analytics":
        return "Analytics";
      case "reviews":
        return "Reviews";
      case "log":
        return "Activity Log";
      case "settings":
        return "Settings";
      case "help":
        return "Help & Documentation";
      case "pricing":
        return "Pricing Plans";
      default:
        return "Decisionr.xyz";
    }
  };

  /**
   * Get page description for context
   * Provides helpful subtitles to orient the user
   */
  const getPageDescription = () => {
    switch (currentPage) {
      case "dashboard":
        return "Overview of your decision-making journey";
      case "new-decision":
        return "Record a new decision for future reflection";
      case "my-decisions":
        return "All your recorded decisions in one place";
      case "decision-detail":
        return "Detailed view and editing for your decision";
      case "analytics":
        return "Insights and trends from your decisions";
      case "reviews":
        return "Scheduled decision reviews and follow-ups";
      case "log":
        return "Track your activity and export your data";
      case "settings":
        return "Customize your experience";
      case "help":
        return "Documentation, guides, and support";
      case "pricing":
        return "Choose the plan that fits your needs";
      default:
        return "Make better decisions through reflection";
    }
  };

  return (
    <div className="flex w-full items-center justify-between min-w-0">
      {/* LEFT SIDE - Page title */}
      <div className="flex items-center space-x-4 min-w-0 flex-1">
        <div className="min-w-0 flex-1 text-right pr-6">
          <h1 className={`font-semibold text-foreground truncate ${isMobile ? 'text-lg' : ''}`}>
            {getPageTitle()}
          </h1>
          <p className={`text-muted-foreground truncate ${isMobile ? 'text-xs' : 'text-sm'}`}>
            {getPageDescription()}
          </p>
        </div>
        
        {/* Refresh Button */}
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button 
                variant="ghost" 
                size="icon"
                onClick={handleRefresh}
                disabled={isRefreshing}
                className="flex-shrink-0"
              >
                <RefreshCw className={`h-4 w-4 ${isRefreshing ? 'animate-spin' : ''}`} />
              </Button>
            </TooltipTrigger>
            <TooltipContent>
              <p>Refresh</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>

      {/* RIGHT SIDE - Actions and user menu */}
      <div className="flex items-center space-x-4 flex-shrink-0">
        
        {/* Subscription Status Badge */}
        {subscription && (
          <div className={`items-center space-x-2 ${isMobile ? 'hidden' : 'hidden md:flex'}`}>
            <Badge variant="outline" className="flex items-center space-x-1">
              {subscription.plan_type === 'lifetime' && <Crown className="h-3 w-3" />}
              {subscription.plan_type === 'annual' && <Zap className="h-3 w-3" />}
              <span className="capitalize">{subscription.plan_type}</span>
            </Badge>
          </div>
        )}

        {/* Quick New Decision Button - Only show on certain pages */}
        {currentPage !== "new-decision" && (
          <Button 
            onClick={onNewDecision}
            size="sm"
            className="hidden sm:flex items-center space-x-2"
          >
            <Plus className="h-4 w-4" />
            <span>New Decision</span>
          </Button>
        )}

        {/* Mobile New Decision Button */}
        {currentPage !== "new-decision" && (
          <Button 
            onClick={onNewDecision}
            size="sm"
            className="sm:hidden"
          >
            <Plus className="h-4 w-4" />
          </Button>
        )}

        {/* Theme Toggle Button */}
        <ThemeToggle />

        {/* User Profile Dropdown Menu */}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <Button variant="ghost" size="icon" className="rounded-full">
              <User className="h-5 w-5" />
              <span className="sr-only">User menu</span>
            </Button>
          </DropdownMenuTrigger>
          
          <DropdownMenuContent align="end" className="w-56">
            <DropdownMenuLabel>
              <div className="flex flex-col space-y-1">
                <p className="text-sm font-medium leading-none">
                  {user?.email?.split('@')[0] || 'User'}
                </p>
                <p className="text-xs leading-none text-muted-foreground">
                  {user?.email || 'No email'}
                </p>
              </div>
            </DropdownMenuLabel>
            
            <DropdownMenuSeparator />
            
            {/* Subscription Status in Dropdown */}
            {subscription && (
              <>
                <DropdownMenuItem disabled>
                  <div className="flex items-center justify-between w-full">
                    <div className="flex items-center space-x-2">
                      {subscription.plan_type === 'lifetime' && <Crown className="h-3 w-3" />}
                      {subscription.plan_type === 'annual' && <Zap className="h-3 w-3" />}
                      <span className="text-xs capitalize">{subscription.plan_type} Plan</span>
                    </div>
                  </div>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
              </>
            )}
            
            {/* Upgrade Option for Free Users */}
            {subscription?.plan_type === 'free' && onUpgrade && (
              <>
                <DropdownMenuItem onClick={onUpgrade} className="text-primary">
                  <Crown className="mr-2 h-4 w-4" />
                  <span>Upgrade Plan</span>
                </DropdownMenuItem>
                <DropdownMenuSeparator />
              </>
            )}
            
            <DropdownMenuItem onClick={onSettings}>
              <Settings className="mr-2 h-4 w-4" />
              <span>Settings</span>
            </DropdownMenuItem>
            
            <DropdownMenuItem onClick={onHelp}>
              <BookOpen className="mr-2 h-4 w-4" />
              <span>Help & Support</span>
            </DropdownMenuItem>
            
            <DropdownMenuSeparator />
            
            <DropdownMenuItem onClick={onSignOut} className="text-red-600">
              <LogOut className="mr-2 h-4 w-4" />
              <span>Sign out</span>
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </div>
    </div>
  );
}